import React from 'react'
import {
    Dispatch,
    Fragment,
    SetStateAction,
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react'
import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/solid'
import { classNames } from '@progress/kendo-react-common'
import { Tooltip } from '@progress/kendo-react-tooltip';
import { DropPosition } from '../common'
import { ServiceApiItem } from 'types/service'
import { getTranslation } from 'utils/language-helper';
import { useAppContext } from 'context/AppContext';

type SelectorProps = {
    className?: string
    position?: DropPosition
    items: ServiceApiItem[] | any[]
    selected?: ServiceApiItem
    setSelectedServices: Dispatch<SetStateAction<string[]>>
    selectedServices?: any[]
    title?: string
}
export function ServiceSelector(props: SelectorProps) {
    const { translations } = useAppContext()
    const title_label_ref = React.createRef<HTMLSpanElement>()
    const tooltip_ref = React.useRef<Tooltip>(null);
    const [is_title_label_overflowed, toggleTitleLabelOverflow] = useState<boolean>(false)
    const [selected_items, setSelection] = useState<number[]>([])
    const [initialized, setInitialized] = useState(false)
    const plural_title = props.title?.endsWith('s') ? props.title : `${props.title}s`

    useEffect(() => {
        if (!initialized && props?.items?.length) {
            setSelection((prev) => {
                let temp = []
                let counter = props?.items?.length || 0
                for (let i = 0; i < counter; i++) {
                    temp.push(i)
                }
                return temp
            })
            setInitialized(true)
        }
    }, [props.items])

    function handleSelectedItem(idx: number) {
        const selection_idx = selected_items.indexOf(idx)
        const selections = selected_items

        let is_selected = false
        if (selection_idx >= 0) {
            selections.splice(selection_idx, 1)
        } else {
            selections.push(idx)
            is_selected = true
        }
        setSelection(selections)

        if (is_selected)
            props.setSelectedServices((prev) => {
                const item = props.items[idx].name // TODO: we really should refactor this to filter by id instead of name
                return prev.includes(item)
                    ? prev.filter((i) => i !== item)
                    : [...prev, item]
            })
        else
            props.setSelectedServices((prev) =>
                prev.filter((item) => item !== props.items[idx].name)
            )
    }

    useLayoutEffect(() => {
        if (title_label_ref?.current && title_label_ref.current.clientWidth < title_label_ref.current.scrollWidth) {
            toggleTitleLabelOverflow(true)
        } else {
            toggleTitleLabelOverflow(false)
        }
    }, [title_label_ref])

    const toggleSelectAll = () => {
        props?.setSelectedServices((prev) => {
            let prevState = [...prev]
            let temp: string[] = []
            if (prevState.length !== props.items.length) {
                temp = props.items.map((itm) => itm.name)
            }
            return temp
        })

        setSelection((prev) => {
            let temp = []
            let counter = props?.items?.length || 0
            for (let i = 0; i < counter; i++) {
                temp.push(i)
            }
            return prev.length === counter ? [] : temp
        })
    }

    const getServicesTitle = useCallback(() => {
        const is_all = selected_items.length === props.items?.length
        const title_1 = is_all
            ? getTranslation("all_x", translations, { val: getTranslation(plural_title || "", translations) })
            : `${selected_items.length} ${selected_items.length === 1 ? props.title : plural_title}`

        return title_1.includes('undefined' || undefined) ? '' : title_1
    }, [selected_items, props.items, props.title, translations])

    return (
        <Menu
            as="div"
            className={classNames(
                'relative inline-block text-left',
                props.className
            )}
        >
            {({ open }) => (
                <>
                    <Menu.Button
                        className={classNames(
                            'inline-flex justify-center items-center w-full',
                            'rounded-md border shadow-sm p-2 bg-white',
                            'text-sm font-medium text-gray-700',
                            'hover:bg-gray-50 '
                        )}
                    >
                        <i className="feather-filter text-sm mr-2" />
                        <span title={is_title_label_overflowed ? getServicesTitle() : ""} className="truncate max-w-[125px] block"
                            onMouseOver={event => tooltip_ref.current && tooltip_ref.current.handleMouseOver(event)}
                            onMouseOut={event => tooltip_ref.current && tooltip_ref.current.handleMouseOut(event)}
                        >
                            {getServicesTitle()}
                        </span>
                        {is_title_label_overflowed &&
                            <Tooltip ref={tooltip_ref} anchorElement="target" position="top"></Tooltip>
                        }
                        <ChevronDownIcon
                            className="-mr-1 ml-2 h-5 w-5 text-custom-dark-cyan-blue"
                            aria-hidden="true"
                        />
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                        show={open}
                    >
                        <Menu.Items
                            static
                            className={classNames(
                                props.position,
                                'absolute mt-2 w-56',
                                'rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5',
                                'focus:outline-none'
                            )}
                        >
                            <Menu.Item key={'allServices'}>
                                {({ active }) => (
                                    <div
                                        className={classNames(
                                            active
                                                ? 'bg-gray-100 text-gray-900'
                                                : 'text-gray-700',
                                            'block text-sm'
                                        )}
                                    >
                                        <label className="block min-w-0 flex-1 text-gray-500 h-full px-3 py-2"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                toggleSelectAll()
                                            }}>
                                            <input
                                                name="filter_service[]"
                                                type="checkbox"
                                                value={''}
                                                checked={
                                                    selected_items.length === props.items.length
                                                }
                                                readOnly
                                                className="h-4 w-4 mr-2 border-gray-300 rounded text-primary focus:ring-primary-light"
                                            />
                                            All {plural_title}
                                        </label>
                                    </div>
                                )}
                            </Menu.Item>
                            <hr />
                            <div
                                className="py-1 "
                                style={{
                                    paddingTop: '20px',
                                    overflowX: 'auto',
                                    maxHeight: '50vh',
                                }}
                            >
                                {props.items && props.items.length
                                    ? props.items.map(
                                        (i: ServiceApiItem, idx) => {
                                            return (
                                                <Menu.Item key={idx}>
                                                    {({ active }) => (
                                                        <div
                                                            className={classNames(
                                                                active
                                                                    ? 'bg-gray-100 text-gray-900'
                                                                    : 'text-gray-700',
                                                                'block text-sm'
                                                            )}

                                                        >
                                                            <label
                                                                htmlFor={`filter-${idx}`}
                                                                className="flex min-w-0 flex-1 text-gray-500 h-full px-3 py-2"
                                                                onClick={(e) => {
                                                                    e.preventDefault()
                                                                    e.stopPropagation()
                                                                    handleSelectedItem(
                                                                        idx
                                                                    )
                                                                }}
                                                            >
                                                                <input
                                                                    id={`filter-${idx}`}
                                                                    name="filter_service[]"
                                                                    type="checkbox"
                                                                    value={
                                                                        idx
                                                                    }
                                                                    checked={
                                                                        selected_items.indexOf(idx) > -1
                                                                    }
                                                                    readOnly
                                                                    className="h-4 w-4 mr-2 border-gray-300 rounded text-primary focus:ring-primary-light"
                                                                />
                                                                <span>
                                                                    {
                                                                        i.name
                                                                    }
                                                                </span>
                                                            </label>
                                                        </div>
                                                    )}
                                                </Menu.Item>
                                            )
                                        }
                                    )
                                    : ''}
                            </div>
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    )
}

export default ServiceSelector
